import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  TextField,
  Typography,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  CalendarMonthOutlined as CalendarIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import CalendlyEmbed from '../components/CalendlyEmbed';
import RevealPanel from '../components/RevealPanel';
import api from '../config/api';
import {
  submitPartnerInterest,
  submitPartnerDemoRequest,
  submitPartnerDemoBooking,
  getPartnerInterestErrorMessage,
} from '../utils/partnerInterest';
import {
  captureOutreachAttribution,
  calendlyUtmFromAttribution,
  reportOutreachVisit,
  reportOutreachBooked,
  readOutreachAttribution,
} from '../utils/outreachAttribution';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageCanvasSx,
  documentPageContainerSx,
} from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerOutlineBtnSx } from '../components/PartnerListChrome';
import {
  DESTINATION_IMAGES,
  DestinationBand,
  DestinationPlate,
  HeroProductSplit,
  PartnerHero,
  PRODUCT_SHOTS,
  ProductShot,
  TabletShot,
} from '../components/PartnerMarketingPreviews';
import {
  CtaPair,
  ComparisonTable,
  EnterpriseCallout,
  Eyebrow,
  OnboardingCallout,
  PromiseBox,
  SummaryStrip,
  BillingIntervalToggle,
  pricingFootSx,
  pricingHeadingSx,
  pricingPaperSx,
  pricingSubSx,
  pricingWrapSx,
} from '../components/PricingChrome';
import {
  PARTNER_TRIAL_DAYS,
  PARTNER_PROFESSIONAL_ONBOARDING_FEE,
  PARTNER_DEVELOPER_API_MONTHLY,
  buildPartnerLaunchComparison,
  introductionFeeCopyVars,
  partnerSalesMailto,
} from '../config/plans';
import { planNameKey } from '../utils/partnerPlan';
import { developerPublicBase } from '../utils/developerHost';
import { hasPartnerSession, PARTNER_APP_HOME } from '../utils/partnerWorkspace';

const SECTION_IDS = {
  worksWith: 'partner-works-with',
  traveler: 'partner-traveler',
  operator: 'partner-operator',
  crm: 'partner-crm',
  digitalTrip: 'partner-digital-trip',
  live: 'partner-live',
  advisors: 'partner-advisors',
  group: 'partner-group',
  connected: 'partner-connected',
  marketplace: 'partner-marketplace',
  integrations: 'partner-integrations',
  api: 'partner-api',
  ownership: 'partner-ownership',
  afterSale: 'partner-after-sale',
  pricing: 'partner-pricing',
  interest: 'partner-interest',
  demo: 'partner-demo',
};

const NAV_ITEMS = [
  { id: SECTION_IDS.worksWith, key: 'howItWorks' },
  { id: SECTION_IDS.advisors, key: 'forAdvisors' },
  { id: SECTION_IDS.group, key: 'groupTravel' },
  { id: SECTION_IDS.marketplace, key: 'marketplace' },
  { id: SECTION_IDS.api, key: 'api' },
  { id: SECTION_IDS.pricing, key: 'pricing' },
];

const HASH_TO_SECTION = {
  '#partner-works-with': SECTION_IDS.worksWith,
  '#partner-advisors': SECTION_IDS.advisors,
  '#partner-pricing': SECTION_IDS.pricing,
  '#partner-demo': SECTION_IDS.demo,
  '#partner-traveler': SECTION_IDS.traveler,
  '#partner-integrations': SECTION_IDS.integrations,
  '#partner-api': SECTION_IDS.api,
  '#partner-marketplace': SECTION_IDS.marketplace,
  '#partner-after-sale': SECTION_IDS.afterSale,
};

const partnerSectionNavLinkSx = {
  background: 'none',
  border: 0,
  p: 0,
  color: 'rgba(246,239,228,.72)',
  fontSize: 11,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textDecoration: 'none',
  '&:hover': { color: DOCUMENT.ivory },
};

const demoFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff',
    borderRadius: 2,
  },
  '& .MuiInputBase-input': {
    color: '#111',
    WebkitTextFillColor: '#111',
    '&:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #fff inset',
      WebkitTextFillColor: '#111',
      caretColor: '#111',
    },
  },
};

const sectionHeadingSx = {
  fontFamily: 'var(--font-display), Playfair Display, serif',
  fontWeight: 700,
  fontSize: { xs: '1.75rem', md: '2.4rem' },
  mb: 2,
  color: '#111',
};

const sectionBodySx = {
  fontSize: '1.1rem',
  lineHeight: 1.75,
  color: '#465066',
};

const Section = ({ id, alt, children }) => (
  <Box
    id={id}
    sx={{
      py: { xs: 8, md: 10 },
      bgcolor: alt ? '#f4f1ef' : '#fff',
      scrollMarginTop: { xs: 120, md: 156 },
      maxWidth: '100%',
      overflowX: 'hidden',
    }}
  >
    <Container
      maxWidth={PAGE_CONTAINER_MAX_WIDTH}
      sx={{ ...documentPageContainerSx, maxWidth: '100%', minWidth: 0 }}
    >
      {children}
    </Container>
  </Box>
);

const Split = ({ flip = false, media, children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: flip ? '1.1fr 1fr' : '1fr 1.1fr' },
      gap: { xs: 4, md: 6 },
      alignItems: 'center',
      minWidth: 0,
      maxWidth: '100%',
    }}
  >
    <Box sx={{ order: { xs: 2, md: flip ? 1 : 2 }, minWidth: 0, maxWidth: '100%' }}>{media}</Box>
    <Box sx={{ order: { xs: 1, md: flip ? 2 : 1 }, minWidth: 0, maxWidth: '100%' }}>{children}</Box>
  </Box>
);

const FeatureChips = ({ items }) => (
  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.25} sx={{ mt: 3 }}>
    {items.map((item) => (
      <Chip
        key={item}
        label={item}
        sx={{
          bgcolor: 'rgba(89, 23, 22, 0.08)',
          color: '#591716',
          fontWeight: 600,
          borderRadius: 1,
        }}
      />
    ))}
  </Stack>
);

const CheckList = ({ items }) => (
  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 3 }}>
    {items.map((item) => (
      <Box
        component="li"
        key={item}
        sx={{
          display: 'flex',
          gap: 1.5,
          py: 1.1,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          fontSize: 15,
          color: '#111',
        }}
      >
        <Box component="span" sx={{ color: '#591716', fontWeight: 700 }}>✓</Box>
        <span>{item}</span>
      </Box>
    ))}
  </Box>
);

const AFTER_SALE_ROWS = [
  ['itineraryToday', 'itineraryWith'],
  ['updatesToday', 'updatesWith'],
  ['questionsToday', 'questionsWith'],
  ['changesToday', 'changesWith'],
  ['documentsToday', 'documentsWith'],
  ['groupToday', 'groupWith'],
];

const honeypotFieldSx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: 0,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const cellSx = {
  textAlign: 'left',
  px: { xs: 2, md: 3 },
  py: { xs: 1.75, md: 2.15 },
  borderBottom: '1px solid rgba(0,0,0,0.06)',
  verticalAlign: 'top',
  width: '50%',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
};

const PainComparison = ({ t }) => (
  <Box
    sx={{
      width: '100%',
      maxWidth: 860,
      minWidth: 0,
      mt: { xs: 4, md: 5 },
      overflowX: 'hidden',
      border: '1px solid rgba(0,0,0,0.06)',
      bgcolor: '#fff',
    }}
  >
  <Box
    component="table"
    aria-label={t('partnerPage.afterSale.title')}
    sx={{
      width: '100%',
      minWidth: 0,
      tableLayout: 'fixed',
      borderCollapse: 'collapse',
    }}
  >
    <Box component="thead">
      <Box component="tr">
        <Box
          component="th"
          scope="col"
          sx={{
            ...cellSx,
            py: 2,
            fontSize: 12,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: '#6F5E62',
            fontWeight: 600,
            borderBottom: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          {t('partnerPage.afterSale.today')}
        </Box>
        <Box
          component="th"
          scope="col"
          sx={{
            ...cellSx,
            py: 2,
            fontSize: 12,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: '#591716',
            fontWeight: 700,
            bgcolor: 'rgba(89, 23, 22, 0.05)',
            borderBottom: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          {t('partnerPage.afterSale.withDvn')}
        </Box>
      </Box>
    </Box>
    <Box component="tbody">
      {AFTER_SALE_ROWS.map(([todayKey, withKey]) => (
        <Box component="tr" key={todayKey}>
          <Box
            component="td"
            sx={{
              ...cellSx,
              fontSize: { xs: 14, md: 16 },
              color: '#6F5E62',
            }}
          >
            {t(`partnerPage.afterSale.${todayKey}`)}
          </Box>
          <Box
            component="td"
            sx={{
              ...cellSx,
              fontSize: { xs: 14, md: 16 },
              fontWeight: 600,
              color: '#111',
              bgcolor: 'rgba(89, 23, 22, 0.05)',
            }}
          >
            {t(`partnerPage.afterSale.${withKey}`)}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
  </Box>
);

const Partner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [demoForm, setDemoForm] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    location: '',
    businessDescription: '',
  });
  const [demoHoneypot, setDemoHoneypot] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [demoReady, setDemoReady] = useState(false);
  const [yearlyPricing, setYearlyPricing] = useState(false);
  const [outreachAttribution, setOutreachAttribution] = useState(null);
  const partnerName = useCallback((id) => t(planNameKey(id)), [t]);
  const pricingInterval = yearlyPricing ? 'yearly' : 'monthly';
  const partnerComparison = useMemo(
    () => buildPartnerLaunchComparison({ t, interval: pricingInterval, partnerName }),
    [t, pricingInterval, partnerName]
  );
  const salesMailto = partnerSalesMailto();

  useEffect(() => {
    const attribution = captureOutreachAttribution(window.location.search);
    setOutreachAttribution(attribution);
    if (attribution?.email || attribution?.name || attribution?.company) {
      setDemoForm((prev) => ({
        ...prev,
        email: prev.email || attribution.email || '',
        name: prev.name || attribution.name || '',
        business: prev.business || attribution.company || '',
      }));
    }
    const visitType = attribution?.content === 'demo' ? 'demo_click' : 'partner_visit';
    reportOutreachVisit(api, { type: visitType });
  }, []);

  useEffect(() => {
    if (!demoReady) return undefined;
    // When the demo form completes, also mark a demo_click if we have attribution
    const attribution = readOutreachAttribution();
    if (attribution?.sendId || attribution?.email) {
      reportOutreachVisit(api, { type: 'demo_click' });
    }
    return undefined;
  }, [demoReady]);

  const handleInterestSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      await submitPartnerInterest(api, {
        email,
        companyName,
        website_url: honeypot,
        source: 'partner_page',
      });
      setSuccess(true);
      setEmail('');
      setCompanyName('');
    } catch (err) {
      setError(getPartnerInterestErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToDemo = () => scrollToSection(SECTION_IDS.demo);

  useEffect(() => {
    const html = document.documentElement;
    const { body } = document;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflowX: html.style.overflowX,
      htmlMaxWidth: html.style.maxWidth,
      bodyOverflowX: body.style.overflowX,
      bodyMaxWidth: body.style.maxWidth,
      rootOverflowX: root?.style.overflowX,
      rootMaxWidth: root?.style.maxWidth,
    };
    html.style.overflowX = 'clip';
    html.style.maxWidth = '100%';
    body.style.overflowX = 'clip';
    body.style.maxWidth = '100%';
    if (root) {
      root.style.overflowX = 'clip';
      root.style.maxWidth = '100%';
    }
    return () => {
      html.style.overflowX = prev.htmlOverflowX;
      html.style.maxWidth = prev.htmlMaxWidth;
      body.style.overflowX = prev.bodyOverflowX;
      body.style.maxWidth = prev.bodyMaxWidth;
      if (root) {
        root.style.overflowX = prev.rootOverflowX;
        root.style.maxWidth = prev.rootMaxWidth;
      }
    };
  }, []);

  useEffect(() => {
    const sectionId = HASH_TO_SECTION[hash];
    if (!sectionId) return undefined;
    const timer = window.setTimeout(() => scrollToSection(sectionId), 0);
    return () => window.clearTimeout(timer);
  }, [hash]);

  const handleDemoFieldChange = (field) => (event) => {
    setDemoForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleDemoSubmit = async (event) => {
    event.preventDefault();
    setDemoError('');
    setDemoLoading(true);

    try {
      await submitPartnerDemoRequest(api, {
        ...demoForm,
        website_url: demoHoneypot,
      });
      setDemoReady(true);
    } catch (err) {
      setDemoError(getPartnerInterestErrorMessage(err, t));
    } finally {
      setDemoLoading(false);
    }
  };

  const lifecycleSteps = [
    t('partnerPage.digitalTrip.before'),
    t('partnerPage.digitalTrip.booking'),
    t('partnerPage.digitalTrip.during'),
    t('partnerPage.digitalTrip.after'),
  ];

  const worksWithSteps = [
    {
      title: t('partnerPage.worksWith.website'),
      body: t('partnerPage.worksWith.websiteBody'),
    },
    {
      title: t('partnerPage.worksWith.dvn'),
      body: t('partnerPage.worksWith.dvnBody'),
    },
    {
      title: t('partnerPage.worksWith.travelers'),
      body: t('partnerPage.worksWith.travelersBody'),
    },
  ];

  if (hasPartnerSession()) {
    return <Navigate to={PARTNER_APP_HOME} replace />;
  }

  return (
    <Box
      data-testid="partner-page"
      sx={{
        ...documentPageCanvasSx,
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        overscrollBehaviorX: 'none',
      }}
    >
      <SEOHead
        title={t('partnerPage.seo.title')}
        description={t('partnerPage.seo.description')}
        keywords={t('partnerPage.seo.keywords')}
        canonical="https://dejavunomad.com/partner"
      />

      <Box
        component="nav"
        aria-label={t('partnerPage.nav.label')}
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 2.25,
          py: 1.25,
          px: 3,
          bgcolor: DOCUMENT.nav,
          borderBottom: `1px solid ${DOCUMENT.navRule}`,
        }}
      >
        <Box component={RouterLink} to="/" sx={partnerSectionNavLinkSx}>
          {t('partnerPage.nav.home')}
        </Box>
        {NAV_ITEMS.map((item) => (
          <Box
            key={item.id}
            component="button"
            type="button"
            onClick={() => scrollToSection(item.id)}
            sx={partnerSectionNavLinkSx}
          >
            {t(`partnerPage.nav.${item.key}`)}
          </Box>
        ))}
        <Box component={RouterLink} to="/partner/login" sx={partnerSectionNavLinkSx}>
          {t('partnerPage.nav.signIn')}
        </Box>
        <Box component={RouterLink} to="/partner/signup" sx={partnerSectionNavLinkSx}>
          {t('partnerPage.nav.signUp')}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={scrollToDemo}
          sx={partnerSectionNavLinkSx}
        >
          {t('partnerPage.nav.bookADemo')}
        </Box>
      </Box>

      <PartnerHero
        eyebrow={t('partnerPage.hero.badge')}
        title={(
          <>
            {t('partnerPage.hero.title')}
            <Box component="span" sx={{ display: 'block', color: '#C9A961' }}>
              {t('partnerPage.hero.titleLine2')}
            </Box>
          </>
        )}
        subtitle={t('partnerPage.hero.subtitle')}
        alongside={t('partnerPage.hero.alongside')}
        stage={(
          <HeroProductSplit
            claimLine1={t('partnerPage.hero.splitLine1')}
            claimLine2={t('partnerPage.hero.splitLine2')}
            portalLabel={t('partnerPage.hero.portalLabel')}
            travelerLabel={t('partnerPage.hero.travelerLabel')}
            portalSrc={PRODUCT_SHOTS.partnerTrips}
            portalAlt={t('partnerPage.media.dashboardAlt')}
            phoneSrc={PRODUCT_SHOTS.mobileItinerary}
            phoneAlt={t('partnerPage.media.itineraryAlt')}
          />
        )}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            size="large"
            onClick={() => scrollToSection(SECTION_IDS.traveler)}
            sx={{
              ...partnerOxbloodBtnSx,
              width: { xs: '100%', sm: 'auto' },
              bgcolor: '#C9A961',
              color: DOCUMENT.nav,
              '&:hover': { bgcolor: '#DCC183', boxShadow: 'none' },
            }}
          >
            {t('partnerPage.hero.seeExperience')}
          </Button>
          <Button
            variant="outlined"
            size="large"
            component={RouterLink}
            to="/partner/signup"
            sx={{
              ...partnerOutlineBtnSx,
              width: { xs: '100%', sm: 'auto' },
              color: DOCUMENT.ivory,
              borderColor: 'rgba(246,239,228,.45)',
              '&:hover': {
                background: 'rgba(246,239,228,0.08)',
                borderColor: '#C9A961',
                boxShadow: 'none',
              },
            }}
          >
            {t('partnerPage.hero.signUp')}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={scrollToDemo}
            startIcon={<CalendarIcon />}
            sx={{
              ...partnerOutlineBtnSx,
              width: { xs: '100%', sm: 'auto' },
              color: DOCUMENT.ivory,
              borderColor: 'rgba(246,239,228,.45)',
              '&:hover': {
                background: 'rgba(246,239,228,0.08)',
                borderColor: '#C9A961',
                boxShadow: 'none',
              },
            }}
          >
            {t('partnerPage.hero.bookDemo')}
          </Button>
        </Stack>
      </PartnerHero>

      <Section id={SECTION_IDS.worksWith}>
        <RevealPanel direction="left">
          <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
            {t('partnerPage.worksWith.title')}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr auto 1fr' },
              gap: { xs: 2.5, md: 2 },
              alignItems: 'stretch',
              mt: 4,
            }}
          >
            {worksWithSteps.map((step, index) => (
              <React.Fragment key={step.title}>
                <Box
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    bgcolor: '#f4f1ef',
                    border: '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      color: '#591716',
                      fontWeight: 700,
                      mb: 1.25,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography sx={{ ...sectionBodySx, fontSize: '1rem', mb: 0 }}>
                    {step.body}
                  </Typography>
                </Box>
                {index < worksWithSteps.length - 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#591716',
                      fontSize: { xs: 22, md: 28 },
                      transform: { xs: 'rotate(90deg)', md: 'none' },
                    }}
                    aria-hidden
                  >
                    →
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
          <Typography sx={{ ...sectionBodySx, mt: 4, fontWeight: 600, color: '#111' }}>
            {t('partnerPage.worksWith.footnote')}
          </Typography>
        </RevealPanel>
      </Section>

      <Section id={SECTION_IDS.traveler} alt>
        <RevealPanel direction="right">
          <Split
            media={(
              <TabletShot
                src={PRODUCT_SHOTS.ipadItinerary}
                alt={t('partnerPage.media.itineraryAlt')}
                caption={t('partnerPage.media.captionItinerary')}
              />
            )}
          >
            <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
              {t('partnerPage.product.travelerTitle')}
            </Typography>
            <Typography sx={sectionBodySx}>
              {t('partnerPage.product.travelerSubtitle')}
            </Typography>
          </Split>
        </RevealPanel>
      </Section>

      <Section id={SECTION_IDS.operator}>
        <RevealPanel direction="left">
          <Split
            flip
            media={(
              <ProductShot
                src={PRODUCT_SHOTS.partnerTrips}
                alt={t('partnerPage.media.dashboardAlt')}
                caption={t('partnerPage.media.captionDashboard')}
              />
            )}
          >
            <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
              {t('partnerPage.product.partnerTitle')}
            </Typography>
            <Typography sx={sectionBodySx}>
              {t('partnerPage.product.partnerSubtitle')}
            </Typography>
          </Split>
        </RevealPanel>
      </Section>

      <Section id={SECTION_IDS.crm}>
        <RevealPanel direction="right">
          <Box sx={{ maxWidth: '60ch' }}>
            <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
              {t('partnerPage.crm.title')}
            </Typography>
            <Typography
              sx={{
                fontFamily: DOCUMENT.display,
                fontSize: { xs: 22, md: 28 },
                fontWeight: 400,
                lineHeight: 1.2,
                color: '#591716',
                mt: 1.5,
                mb: 2,
              }}
            >
              {t('partnerPage.crm.subtitle')}
            </Typography>
            <Typography sx={sectionBodySx}>
              {t('partnerPage.crm.body')}
            </Typography>
          </Box>
        </RevealPanel>
      </Section>

      <Section id={SECTION_IDS.digitalTrip} alt>
        <RevealPanel direction="right">
          <Split
            media={(
              <DestinationPlate
                {...DESTINATION_IMAGES.digital}
                alt={t('partnerPage.media.digitalAlt')}
                ratio="4 / 3"
              />
            )}
          >
            <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
              {t('partnerPage.digitalTrip.title')}
            </Typography>
            <Typography sx={sectionBodySx}>
              {t('partnerPage.digitalTrip.description')}
            </Typography>
          </Split>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
              gap: 1.5,
              mt: 4,
            }}
          >
            {lifecycleSteps.map((step, index) => (
              <Box
                key={step}
                sx={{
                  p: 2.5,
                  bgcolor: '#fff',
                  border: '1px solid rgba(0,0,0,0.06)',
                }}
              >
                <Typography sx={{ fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#591716', mb: 1 }}>
                  {String(index + 1).padStart(2, '0')}
                </Typography>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22 }}>
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>
        </RevealPanel>
      </Section>

      <Section id={SECTION_IDS.live}>
        <RevealPanel direction="left">
          <Split
            flip
            media={(
              <TabletShot
                src={PRODUCT_SHOTS.ipadItinerary}
                alt={t('partnerPage.media.itineraryAlt')}
                caption={t('partnerPage.media.captionItinerary')}
                align="start"
              />
            )}
          >
            <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
              {t('partnerPage.live.title')}
            </Typography>
            <Typography sx={sectionBodySx}>
              {t('partnerPage.live.description')}
            </Typography>
            <CheckList
              items={[
                t('partnerPage.live.updates'),
                t('partnerPage.live.notifications'),
                t('partnerPage.live.documents'),
                t('partnerPage.live.maps'),
              ]}
            />
          </Split>
        </RevealPanel>
      </Section>

      <Section id={SECTION_IDS.advisors} alt>
        <RevealPanel direction="right">
          <Split
            media={(
              <TabletShot
                src={PRODUCT_SHOTS.ipadItinerary}
                alt={t('partnerPage.media.itineraryAlt')}
                caption={t('partnerPage.media.captionAdvisors')}
              />
            )}
          >
            <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
              {t('partnerPage.advisors.title')}
            </Typography>
            <Typography sx={sectionBodySx}>
              {t('partnerPage.advisors.description')}
            </Typography>
            <FeatureChips
              items={[
                t('partnerPage.advisors.intakeToLive'),
                t('partnerPage.advisors.pipelineBoard'),
                t('partnerPage.advisors.proposalViews'),
                t('partnerPage.advisors.clientMemory'),
                t('partnerPage.advisors.travelReminders'),
                t('partnerPage.advisors.clientTrips'),
                t('partnerPage.advisors.noCommission'),
              ]}
            />
          </Split>
        </RevealPanel>
      </Section>

      <Section id={SECTION_IDS.group}>
        <RevealPanel direction="left">
          <Split
            flip
            media={(
              <DestinationPlate
                {...DESTINATION_IMAGES.group}
                alt={t('partnerPage.media.groupAlt')}
                ratio="4 / 3"
              />
            )}
          >
            <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
              {t('partnerPage.group.title')}
            </Typography>
            <Typography sx={sectionBodySx}>
              {t('partnerPage.group.description')}
            </Typography>
            <FeatureChips
              items={[
                t('partnerPage.group.itinerary'),
                t('partnerPage.group.announcements'),
                t('partnerPage.group.chat'),
                t('partnerPage.group.meetings'),
                t('partnerPage.group.maps'),
                t('partnerPage.group.lists'),
                t('partnerPage.group.changes'),
              ]}
            />
          </Split>
        </RevealPanel>
      </Section>

      <Box id={SECTION_IDS.connected} sx={{ scrollMarginTop: { xs: 120, md: 156 } }}>
        <DestinationBand
          {...DESTINATION_IMAGES.connected}
          alt={t('partnerPage.media.connectedAlt')}
        >
          <RevealPanel direction="left">
            <Typography
              variant="h3"
              component="h2"
              sx={{ ...sectionHeadingSx, color: '#FFF8EE', maxWidth: 640 }}
            >
              {t('partnerPage.connected.title')}
            </Typography>
            <Typography sx={{ ...sectionBodySx, color: 'rgba(246,239,228,.82)', maxWidth: 640, mb: 0 }}>
              {t('partnerPage.connected.description')}
            </Typography>
          </RevealPanel>
        </DestinationBand>
      </Box>

      <Section id={SECTION_IDS.marketplace} alt>
        <RevealPanel direction="right">
          <Split
            media={(
              <TabletShot
                src={PRODUCT_SHOTS.ipadPackages}
                alt={t('partnerPage.media.packagesAlt')}
                caption={t('partnerPage.media.captionPackages')}
              />
            )}
          >
            <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
              {t('partnerPage.marketplace.title')}
            </Typography>
            <Typography sx={{ ...sectionBodySx, mb: 4 }}>
              {t('partnerPage.marketplace.description')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              {[
                {
                  label: t('partnerPage.marketplace.yourCustomers'),
                  value: t('partnerPage.marketplace.yourCustomersValue'),
                },
                {
                  label: t('partnerPage.marketplace.ourCustomers'),
                  value: t('partnerPage.marketplace.ourCustomersValue'),
                },
              ].map((card) => (
                <Box
                  key={card.label}
                  sx={{
                    p: 3,
                    bgcolor: '#fff',
                    border: '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <Typography sx={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#591716', mb: 1 }}>
                    {card.label}
                  </Typography>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 26 }}>
                    {card.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Split>
        </RevealPanel>
      </Section>

      <Section id={SECTION_IDS.integrations}>
        <RevealPanel direction="left">
          <Split
            flip
            media={(
              <DestinationPlate
                {...DESTINATION_IMAGES.integrations}
                alt={t('partnerPage.media.integrationsAlt')}
                ratio="4 / 3"
              />
            )}
          >
            <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
              {t('partnerPage.integrations.title')}
            </Typography>
            <Typography sx={sectionBodySx}>
              {t('partnerPage.integrations.description')}
            </Typography>
            <FeatureChips
              items={[
                t('partnerPage.integrations.branding'),
                t('partnerPage.integrations.domain'),
                t('partnerPage.integrations.api'),
                t('partnerPage.integrations.sso'),
                t('partnerPage.integrations.booking'),
                t('partnerPage.integrations.automation'),
                t('partnerPage.integrations.capacity'),
              ]}
            />
          </Split>
        </RevealPanel>
      </Section>

      <Section id={SECTION_IDS.api} alt>
        <RevealPanel direction="right">
          <Split
            media={(
              <DestinationPlate
                {...DESTINATION_IMAGES.api}
                alt={t('partnerPage.media.apiAlt')}
                ratio="4 / 3"
              />
            )}
          >
            <Typography
              sx={{
                fontSize: 12,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: DOCUMENT.ink3,
                mb: 1.5,
                fontWeight: 600,
              }}
            >
              {t('partnerPage.api.eyebrow')}
            </Typography>
            <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
              {t('partnerPage.api.title')}
            </Typography>
            <Typography sx={sectionBodySx}>
              {t('partnerPage.api.description')}
            </Typography>
            <CheckList
              items={[
                t('partnerPage.api.widgets'),
                t('partnerPage.api.rest'),
                t('partnerPage.api.webhooks'),
                t('partnerPage.api.sandbox'),
              ]}
            />
            <Typography sx={{ ...sectionBodySx, mt: 3, mb: 0, fontSize: '0.95rem', maxWidth: '58ch' }}>
              {t('partnerPage.api.note', { fee: PARTNER_DEVELOPER_API_MONTHLY })}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3.5 }}>
              <Button
                component="a"
                href={`${developerPublicBase()}/docs`}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<ArrowIcon />}
                sx={partnerOxbloodBtnSx}
              >
                {t('partnerPage.api.ctaDocs')}
              </Button>
              <Button
                onClick={() => scrollToSection(SECTION_IDS.pricing)}
                sx={partnerOutlineBtnSx}
              >
                {t('partnerPage.api.ctaPricing')}
              </Button>
            </Stack>
          </Split>
        </RevealPanel>
      </Section>

      <Section id={SECTION_IDS.ownership}>
        <RevealPanel direction="right">
          <Typography variant="h3" component="h2" sx={sectionHeadingSx}>
            {t('partnerPage.ownership.title')}
          </Typography>
          <Typography sx={{ ...sectionBodySx, maxWidth: 720 }}>
            {t('partnerPage.ownership.description')}
          </Typography>
          <CheckList
            items={[
              t('partnerPage.ownership.noMigration'),
              t('partnerPage.ownership.noReplace'),
              t('partnerPage.ownership.noCommission'),
            ]}
          />
        </RevealPanel>
      </Section>

      <Section id={SECTION_IDS.afterSale}>
        <RevealPanel direction="left">
          <Typography variant="h3" component="h2" sx={{ ...sectionHeadingSx, maxWidth: 720 }}>
            {t('partnerPage.afterSale.title')}
          </Typography>
          <PainComparison t={t} />
        </RevealPanel>
      </Section>

      <Box
        id={SECTION_IDS.pricing}
        sx={{ py: { xs: 7, md: 10 }, borderTop: `1px solid ${DOCUMENT.rule}`, scrollMarginTop: { xs: 120, md: 156 } }}
      >
        <Box sx={pricingWrapSx}>
          <Box sx={pricingPaperSx}>
            <Eyebrow>{t('partnerPage.pricing.eyebrow')}</Eyebrow>
            <Typography component="h2" sx={{ ...pricingHeadingSx, fontSize: { xs: 28, md: 40 } }}>
              {t('partnerPage.pricing.title')}
            </Typography>
            <Typography sx={{ ...pricingSubSx, maxWidth: '58ch', fontSize: 13, color: 'text.secondary' }}>
              {t('partnerPage.pricing.disclaimer')}
            </Typography>
            <Typography sx={{ ...pricingSubSx, maxWidth: '58ch' }}>
              {t('partnerPage.pricing.subtitle')}
            </Typography>
            <Typography sx={{ ...pricingSubSx, maxWidth: '58ch' }}>
              {t('partnerPage.pricing.subtitleDetail', introductionFeeCopyVars())}
            </Typography>

            <BillingIntervalToggle
              value={pricingInterval}
              onChange={(next) => setYearlyPricing(next === 'yearly')}
              ariaLabel={t('partnerPage.pricing.billingLabel')}
              monthlyLabel={t('partnerPage.pricing.monthly')}
              yearlyLabel={t('partnerPage.pricing.yearly')}
              yearlySavingLabel={t('partnerPage.pricing.save')}
              sx={{ mb: { xs: 2.5, md: 3 } }}
            />

            <ComparisonTable
              caption={t('pricingPage.compare.partnerCaption')}
              columns={partnerComparison.columns}
              rows={partnerComparison.rows}
              yesLabel={t('pricingPage.compare.included')}
              noLabel={t('pricingPage.compare.notIncluded')}
            />

            <EnterpriseCallout
              title={t('pricingPage.enterprise.title')}
              body={t('pricingPage.enterprise.body')}
              ctaHref={salesMailto}
              ctaLabel={t('pricingPage.enterprise.cta')}
            />

            <OnboardingCallout
              title={t('pricingPage.onboarding.title', { fee: PARTNER_PROFESSIONAL_ONBOARDING_FEE })}
              body={t('pricingPage.onboarding.body')}
              ctaHref={salesMailto}
              ctaLabel={t('pricingPage.onboarding.cta')}
            />

            <OnboardingCallout
              eyebrow={t('pricingPage.developerApi.eyebrow')}
              title={t('pricingPage.developerApi.title', { fee: PARTNER_DEVELOPER_API_MONTHLY })}
              body={t('pricingPage.developerApi.body')}
              enterpriseNote={t('pricingPage.developerApi.note')}
              ctaHref={`${developerPublicBase()}/docs`}
              ctaLabel={t('pricingPage.developerApi.ctaDocs')}
              secondaryCtaHref="/partner/billing"
              secondaryCtaLabel={t('pricingPage.developerApi.ctaAdd')}
            />

            <PromiseBox title={t('partnerPage.pricing.ownershipTitle')}>
              {t('partnerPage.pricing.ownershipBody')}
            </PromiseBox>

            <SummaryStrip
              items={[
                { label: t('pricingPage.summary.own'), value: t('pricingPage.rates.ownValue'), accent: true },
                { label: t('pricingPage.summary.trips'), value: t('pricingPage.compare.unlimited') },
                {
                  label: t('pricingPage.summary.trial'),
                  value: t('pricingPage.summary.trialValue', { trialDays: PARTNER_TRIAL_DAYS }),
                },
              ]}
            />

            <CtaPair
              primary={t('pricingPage.partner.cta')}
              onPrimary={() => navigate('/partner/signup')}
              secondary={t('pricingPage.partner.secondary')}
              onSecondary={scrollToDemo}
            />

            <Typography sx={pricingFootSx}>
              {t('partnerPage.pricing.note', { count: PARTNER_TRIAL_DAYS, ...introductionFeeCopyVars() })}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        id={SECTION_IDS.interest}
        sx={{
          py: { xs: 8, md: 10 },
          background: 'linear-gradient(135deg, #111111 0%, #591716 55%, #313848 100%)',
          color: '#fff',
        }}
      >
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
          <RevealPanel direction="left">
            <Box sx={{ textAlign: 'center', mb: 4, maxWidth: 720, mx: 'auto' }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontFamily: 'var(--font-display), Playfair Display, serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.9rem', md: '2.5rem' },
                  mb: 1.5,
                }}
              >
                {t('partnerPage.interest.title')}
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>
                {t('partnerPage.interest.subtitle')}
              </Typography>
            </Box>

            {success ? (
              <Alert
                severity="success"
                sx={{ maxWidth: 480, mx: 'auto', borderRadius: 2 }}
              >
                {t('partnerPage.interest.success')}
              </Alert>
            ) : (
              <Box
                component="form"
                onSubmit={handleInterestSubmit}
                sx={{
                  position: 'relative',
                  maxWidth: 480,
                  mx: 'auto',
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  bgcolor: '#fff',
                  boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
                  color: '#111',
                }}
              >
                <TextField
                  name="website_url"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  sx={honeypotFieldSx}
                />
                <Stack spacing={2}>
                  <TextField
                    required
                    fullWidth
                    type="email"
                    placeholder={t('partnerPage.interest.emailLabel')}
                    aria-label={t('partnerPage.interest.emailLabel')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f8f9fa',
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    placeholder={t('partnerPage.interest.companyLabel')}
                    aria-label={t('partnerPage.interest.companyLabel')}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f8f9fa',
                      },
                    }}
                  />
                  {error && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {error}
                    </Alert>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      py: 1.4,
                      borderRadius: 2,
                      mt: 0.5,
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      t('partnerPage.interest.submit')
                    )}
                  </Button>
                </Stack>
              </Box>
            )}
          </RevealPanel>
        </Container>
      </Box>

      <Box id={SECTION_IDS.demo} sx={{ py: { xs: 8, md: 10 }, bgcolor: '#fff', scrollMarginTop: { xs: 120, md: 156 }, maxWidth: '100%', overflowX: 'hidden' }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
          <RevealPanel direction="right">
            <Box sx={{ textAlign: 'center', mb: 4, maxWidth: 720, mx: 'auto' }}>
              <Chip
                icon={<CalendarIcon />}
                label={t('partnerPage.demo.badge')}
                sx={{ mb: 2, fontWeight: 600, bgcolor: 'rgba(89,23,22,0.08)', color: '#591716' }}
              />
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontFamily: 'var(--font-display), Playfair Display, serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.9rem', md: '2.5rem' },
                  mb: 1.5,
                  color: '#111',
                }}
              >
                {t('partnerPage.demo.title')}
              </Typography>
              <Typography sx={{ color: '#465066', mb: 4 }}>
                {t('partnerPage.demo.subtitle')}
              </Typography>
            </Box>
            {demoReady ? (
              <Box sx={{ maxWidth: 720, mx: 'auto' }}>
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                  {t('partnerPage.demo.ready')}
                </Alert>
                <CalendlyEmbed
                  prefill={{
                    name: demoForm.name,
                    email: demoForm.email,
                  }}
                  utm={calendlyUtmFromAttribution(outreachAttribution)}
                  onEventScheduled={(payload) => {
                    reportOutreachBooked(api, {
                      email: payload.email || demoForm.email,
                      name: payload.name || demoForm.name,
                      scheduledAt: payload.scheduledAt,
                      timezone: payload.timezone,
                    });
                    submitPartnerDemoBooking(api, {
                      ...demoForm,
                      email: payload.email || demoForm.email,
                      name: payload.name || demoForm.name,
                      scheduledAt: payload.scheduledAt,
                      timezone: payload.timezone,
                      inviteeNotes: payload.inviteeNotes,
                    }).catch(() => {
                      // Booking email is best-effort; Calendly still confirmed for the guest.
                    });
                  }}
                />
              </Box>
            ) : (
              <Box
                component="form"
                onSubmit={handleDemoSubmit}
                sx={{
                  position: 'relative',
                  maxWidth: 560,
                  mx: 'auto',
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  bgcolor: '#f8f9fa',
                  border: '1px solid rgba(0,0,0,0.08)',
                }}
              >
                <TextField
                  name="website_url"
                  value={demoHoneypot}
                  onChange={(e) => setDemoHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  sx={honeypotFieldSx}
                />
                <Stack spacing={2}>
                  <TextField
                    required
                    fullWidth
                    label={t('partnerPage.demo.fields.name')}
                    value={demoForm.name}
                    onChange={handleDemoFieldChange('name')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  <TextField
                    required
                    fullWidth
                    label={t('partnerPage.demo.fields.business')}
                    value={demoForm.business}
                    onChange={handleDemoFieldChange('business')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  <TextField
                    required
                    fullWidth
                    type="email"
                    label={t('partnerPage.demo.fields.email')}
                    value={demoForm.email}
                    onChange={handleDemoFieldChange('email')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  <TextField
                    fullWidth
                    type="tel"
                    label={t('partnerPage.demo.fields.phone')}
                    value={demoForm.phone}
                    onChange={handleDemoFieldChange('phone')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  <TextField
                    fullWidth
                    label={t('partnerPage.demo.fields.location')}
                    value={demoForm.location}
                    onChange={handleDemoFieldChange('location')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label={t('partnerPage.demo.fields.businessDescription')}
                    value={demoForm.businessDescription}
                    onChange={handleDemoFieldChange('businessDescription')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  {demoError && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {demoError}
                    </Alert>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={demoLoading}
                    sx={{ textTransform: 'none', fontWeight: 700, py: 1.4, borderRadius: 2 }}
                  >
                    {demoLoading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      t('partnerPage.demo.continue')
                    )}
                  </Button>
                </Stack>
              </Box>
            )}
          </RevealPanel>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 6, md: 8 },
          textAlign: 'center',
          bgcolor: '#f4f1ef',
          borderTop: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'var(--font-display), Playfair Display, serif',
              fontWeight: 700,
              mb: 2,
              maxWidth: 720,
              mx: 'auto',
            }}
          >
            {t('partnerPage.finalCta.title')}
          </Typography>
          <Typography sx={{ color: '#465066', mb: 3, maxWidth: 560, mx: 'auto' }}>
            {t('partnerPage.finalCta.subtitle')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/partner/signup"
              endIcon={<ArrowIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: 2,
              }}
            >
              {t('partnerPage.finalCta.createTrip')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={scrollToDemo}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                borderColor: '#591716',
                color: '#591716',
              }}
            >
              {t('partnerPage.finalCta.bookDemo')}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Partner;
